import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { map } from 'rxjs/operators';
import { ToastService } from '../../../shared/services/toast.service';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { LeadCycleRuleApiService } from '../services/lead-cycle-rule-api.service';
import { LeadCycleRuleRequest, LeadCycleRuleResponse, LeadCycleRuleOperationType } from '../models/lead-cycle-rule.model';
import { LeadCycleApiService } from '../../lead-cycles/services/lead-cycle-api.service';
import { LeadCycleResponse } from '../../lead-cycles/models/lead-cycle.model';
import { LeadAttributeApiService } from '../../lead-attributes/services/lead-attribute-api.service';
import { LeadAttributeResponse } from '../../lead-attributes/models/lead-attribute.model';
import { AuthService } from '../../auth-pages/services/auth.service';

@Component({
  selector: 'app-lead-cycle-rule-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './lead-cycle-rule-form.component.html'
})
export class LeadCycleRuleFormComponent implements OnInit {
  @ViewChild('ruleForm') ruleForm?: NgForm;

  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  ruleId: number | null = null;

  checkingDuplicate = false;
  duplicateOrderError = '';
  valueExpressionValidationError = '';
  private existingRules: LeadCycleRuleResponse[] = [];

  ciclos: LeadCycleResponse[] = [];
  atributos: LeadAttributeResponse[] = [];

  idLeadCycle: number | null = null;
  idLeadAttribute: number | null = null;
  operationType: LeadCycleRuleOperationType = 'SET';
  valueExpression = '';
  // ngModel deja el número en null si el usuario vacía el input.
  executionOrder: number | null = 1;
  isActive = true;

  readonly operationTypeOptions: { value: LeadCycleRuleOperationType; label: string }[] = [
    { value: 'SET', label: 'SET — Asignar valor' },
    { value: 'INCREMENT', label: 'INCREMENT — Incrementar valor' },
    { value: 'COPY_FROM_FIELD', label: 'COPY_FROM_FIELD — Copiar desde campo' }
  ];

  constructor(
    private ruleApi: LeadCycleRuleApiService,
    private cycleApi: LeadCycleApiService,
    private attributeApi: LeadAttributeApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.ruleId = +id;
    }

    this.cargarCatalogos();
  }

  private cargarCatalogos(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    let ciclosLoaded = false;
    let atributosLoaded = false;

    const checkAllLoaded = () => {
      if (ciclosLoaded && atributosLoaded) {
        if (this.isEditing && this.ruleId) {
          this.cargarRegla(this.ruleId, businessId);
        } else {
          this.loading = false;
        }
      }
    };

    this.cycleApi.getCycles(businessId).subscribe({
      next: (data) => {
        this.ciclos = data;
        ciclosLoaded = true;
        checkAllLoaded();
      },
      error: (err) => {
        this.error = 'Error al cargar los ciclos de lead';
        this.loading = false;
        console.error('Error ciclos:', err);
      }
    });

    this.attributeApi.getAttributes(businessId).subscribe({
      next: (data) => {
        this.atributos = data;
        atributosLoaded = true;
        checkAllLoaded();
      },
      error: (err) => {
        this.error = 'Error al cargar los atributos de lead';
        this.loading = false;
        console.error('Error atributos:', err);
      }
    });
  }

  private cargarRegla(id: number, businessId: number): void {
    this.ruleApi.getRuleById(id, businessId).subscribe({
      next: (regla) => {
        this.idLeadCycle = regla.idLeadCycle;
        this.idLeadAttribute = regla.idLeadAttribute;
        this.operationType = regla.operationType;
        this.valueExpression = regla.valueExpression;
        this.executionOrder = regla.executionOrder ?? 1;
        this.isActive = regla.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la regla';
        this.loading = false;
      }
    });
  }

  onValueExpressionChange(value: string): void {
    let filtered = value.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s,.\-_]/g, '');
    if (filtered !== value) {
      this.valueExpressionValidationError = 'Caracteres no permitidos';
    } else {
      this.valueExpressionValidationError = '';
    }
    setTimeout(() => { this.valueExpression = filtered; });
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Regla de Ciclo' : 'Nueva Regla de Ciclo';
  }

  private get selectedCycleName(): string {
    return this.ciclos.find(c => c.id === this.idLeadCycle)?.name ?? String(this.idLeadCycle ?? '');
  }

  get valueExpressionLabel(): string {
    const labels: Record<LeadCycleRuleOperationType, string> = {
      'SET': 'Valor a asignar',
      'INCREMENT': 'Delta (ej: 10)',
      'COPY_FROM_FIELD': 'Campo fuente (ej: idLeadState)'
    };
    return labels[this.operationType];
  }

  get valueExpressionPlaceholder(): string {
    const placeholders: Record<LeadCycleRuleOperationType, string> = {
      'SET': 'ej: true, 100, texto_fijo',
      'INCREMENT': 'ej: 10',
      'COPY_FROM_FIELD': 'ej: idLeadState'
    };
    return placeholders[this.operationType];
  }

  guardar(): void {
    // El botón nunca se deshabilita por validez: si falta algo, el usuario recibe
    // una notificación explícita en vez de un clic mudo.
    const faltantes: string[] = [];
    if (!this.idLeadCycle) faltantes.push('Ciclo de Lead');
    if (!this.idLeadAttribute) faltantes.push('Atributo de Lead');
    if (!this.valueExpression.trim()) faltantes.push(this.valueExpressionLabel);
    if (this.executionOrder === null || this.executionOrder === undefined || this.executionOrder < 1) {
      faltantes.push('Orden de Ejecución');
    }

    if (faltantes.length > 0) {
      this.ruleForm?.form.markAllAsTouched();
      const msg = `Complete los campos requeridos: ${faltantes.join(', ')}.`;
      this.error = msg;
      this.toast.warning({ title: 'Campos incompletos', message: msg });
      return;
    }

    if (this.valueExpressionValidationError) {
      this.toast.warning({ title: 'Valor no válido', message: this.valueExpressionValidationError });
      return;
    }

    this.error = '';
    this.duplicateOrderError = '';
    this.valueExpressionValidationError = '';
    this.checkingDuplicate = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // El orden duplicado NO lo valida el backend (no hay constraint en lead_cycle_rule):
    // si esta consulta falla no podemos seguir a ciegas, o crearíamos el duplicado en silencio.
    this.ruleApi.getRules({ businessId, size: 100 }).pipe(
      map(data => data?.content || [])
    ).subscribe({
      next: (existingRules) => {
        this.checkingDuplicate = false;
        this.existingRules = existingRules;

        const duplicateOrder = existingRules.find(r =>
          r.idLeadCycle === this.idLeadCycle &&
          r.executionOrder === this.executionOrder &&
          (!this.isEditing || r.id !== this.ruleId)
        );

        if (duplicateOrder) {
          this.duplicateOrderError = `Ya existe una regla con el orden "${duplicateOrder.executionOrder}" en este ciclo`;
          this.toast.warning({ title: 'Orden duplicado', message: this.duplicateOrderError });
          return;
        }

        this.executeSave();
      },
      error: (err) => {
        this.checkingDuplicate = false;
        this.error = err.error?.message
          || 'No se pudieron verificar las reglas existentes del ciclo. Intente nuevamente.';
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  private executeSave(): void {
    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: LeadCycleRuleRequest = {
      businessId,
      idLeadCycle: this.idLeadCycle!,
      idLeadAttribute: this.idLeadAttribute!,
      operationType: this.operationType,
      valueExpression: this.valueExpression.trim(),
      executionOrder: Number(this.executionOrder),
      isActive: this.isActive
    };

    if (this.isEditing && this.ruleId) {
      this.ruleApi.updateRule(this.ruleId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Regla de ciclo', `Se actualizó la regla del ciclo "${this.selectedCycleName}".`);
          this.router.navigate(['/config-leads/reglas']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar la regla';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.ruleApi.createRule(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Regla de ciclo', `Se creó la regla del ciclo "${this.selectedCycleName}" correctamente.`);
          this.router.navigate(['/config-leads/reglas']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear la regla';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-leads/reglas']);
  }
}
